import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  HStack,
  Icon,
  Badge,
  Divider,
  Image,
  Flex,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiFilm, FiImage } from "react-icons/fi";
import {
  fetchMediaAnalyzeBlobUrl,
  revokeBlobUrl,
} from "../../../utils/mediaAnalyzeMedia";

/**
 * @param {{ item: object, accent?: string, segmentBg?: string, borderColor?: string }} props
 */
export default function MediaAnalyzeItemCard({
  item,
  accent = "purple",
  segmentBg,
  borderColor,
}) {
  const placeholderBg = useColorModeValue("gray.100", "gray.700");
  const muted = useColorModeValue("gray.600", "gray.400");
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item?.mediaUrl) {
      setSrc(null);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    (async () => {
      try {
        const url = await fetchMediaAnalyzeBlobUrl(item.mediaUrl);
        if (!cancelled) setSrc(url);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setSrc((prev) => {
        revokeBlobUrl(prev);
        return null;
      });
    };
  }, [item?.mediaUrl, item?._id]);

  const isVideoSegment = item?.type === "video_segment";

  return (
    <Box
      p={4}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      bg={segmentBg}
      borderLeftWidth="4px"
      borderLeftColor={`${accent}.400`}
    >
      <HStack mb={2} flexWrap="wrap">
        <Icon
          as={isVideoSegment ? FiFilm : FiImage}
          color={`${accent}.500`}
        />
        <Text fontSize="sm" fontWeight="semibold">
          {isVideoSegment
            ? `Video · ${item.label}`
            : item.fileName || item.label || "Image"}
        </Text>
        {isVideoSegment ? (
          <Badge colorScheme="cyan" fontSize="xs">
            {item.label}
          </Badge>
        ) : null}
      </HStack>

      <Box
        borderRadius="lg"
        overflow="hidden"
        borderWidth="1px"
        borderColor={borderColor}
        mb={3}
        bg={placeholderBg}
        minH="160px"
        maxH="320px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {loading ? (
          <Spinner color={`${accent}.400`} />
        ) : src ? (
          <Image
            src={src}
            alt={item.fileName || item.label || "Media"}
            w="100%"
            maxH="320px"
            objectFit="contain"
          />
        ) : (
          <Flex direction="column" align="center" py={8} px={4}>
            <Icon
              as={isVideoSegment ? FiFilm : FiImage}
              boxSize={10}
              color={`${accent}.300`}
              mb={2}
            />
            <Text fontSize="xs" color={muted} textAlign="center">
              {failed
                ? "Could not load preview."
                : item.mediaUrl
                  ? "Loading preview…"
                  : "No preview stored for this item (re-run analysis to save media)."}
            </Text>
          </Flex>
        )}
      </Box>

      <Divider mb={3} />
      <Text fontSize="sm" lineHeight="tall" whiteSpace="pre-wrap">
        {item.summary}
      </Text>
    </Box>
  );
}
